import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * PWA Install Prompt
 * - Android/Chrome: listens for `beforeinstallprompt` and fires native install
 * - iOS Safari: shows manual "Add to Home Screen" instructions
 * - Hides automatically if app is already installed (display-mode: standalone)
 * - Remembers dismissal in localStorage for 14 days
 */
const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_DAYS = 14;

const isInStandaloneMode = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const wasRecentlyDismissed = () => {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const ageDays = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch {
    return false;
  }
};

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode() || wasRecentlyDismissed()) return;

    // Android/Chrome path
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS path — show manual prompt after 8 seconds of use
    if (isIOS()) {
      const timer = setTimeout(() => setShow(true), 8000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setShow(false);
        setDeferredPrompt(null);
      }
    } else if (isIOS()) {
      setShowIOSInstructions(true);
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setShow(false);
    setShowIOSInstructions(false);
  };

  if (!show) return null;

  // iOS detailed instructions modal
  if (showIOSInstructions) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        data-testid="pwa-ios-instructions"
      >
        <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-bold font-serif text-slate-900">
              Install Union Strong on iPhone
            </h3>
            <button
              onClick={handleDismiss}
              className="text-slate-400 hover:text-slate-700"
              data-testid="pwa-ios-close-btn"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <ol className="space-y-3 text-sm text-slate-700">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">1</span>
              <span>
                Tap the <Share className="w-4 h-4 inline-block mx-1 text-blue-600" />
                <strong>Share</strong> button at the bottom of Safari
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">2</span>
              <span>
                Scroll down and tap <Plus className="w-4 h-4 inline-block mx-1" />
                <strong>Add to Home Screen</strong>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">3</span>
              <span>Tap <strong>Add</strong> in the top-right corner. Done! 🎉</span>
            </li>
          </ol>
          <p className="text-xs text-slate-500 mt-4 italic">
            Note: Works in Safari only. If you're in Chrome on iOS, open this site in Safari first.
          </p>
        </div>
      </div>
    );
  }

  // Bottom banner (Android + iOS first-touch)
  return (
    <div
      className="fixed bottom-16 md:bottom-4 left-2 right-2 md:left-auto md:right-4 md:w-96 z-[9998] bg-slate-900 text-white rounded-xl shadow-2xl border border-amber-500/30 p-4 animate-in slide-in-from-bottom-5"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-amber-500/20 flex items-center justify-center">
          <Download className="w-6 h-6 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">Install Union Strong</p>
          <p className="text-xs text-slate-300 mt-0.5">
            Get the full app on your home screen — works offline, no app store needed.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button
              size="sm"
              onClick={handleInstall}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold h-8 text-xs"
              data-testid="pwa-install-btn"
            >
              {isIOS() ? "Show me how" : "Install"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 text-xs"
              data-testid="pwa-dismiss-btn"
            >
              Not now
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-slate-500 hover:text-white"
          aria-label="Dismiss"
          data-testid="pwa-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
export { PWAInstallPrompt };
